import { Button, Card, ExternalLink, Icon, Text } from '@ui/themed';
import { t } from 'i18next';
import type { I18nPrefix } from '../../../i18n/prefix';
import { useEnableMetafield } from '../hooks/use-enable-metafield';
import type { Metafield, MetafieldGroup } from '../types';

const METAFIELDS_I18N = 'features:platform.pages.metafields_page' satisfies I18nPrefix;

function displayGroupName(name: MetafieldGroup['name']): string {
  switch (name) {
    case 'PRODUCT_VARIANT':
      return t(`${METAFIELDS_I18N}.group_product_variant`);
    case 'CUSTOMER':
      return t(`${METAFIELDS_I18N}.group_customer`);
    case 'ORDER':
      return t(`${METAFIELDS_I18N}.group_order`);
    default:
      return name;
  }
}

function MetafieldRow({ metafield, isFetching }: { metafield: Metafield; isFetching: boolean }) {
  const enableMetafield = useEnableMetafield();

  const isEnabling =
    enableMetafield.isPending ||
    (enableMetafield.isSuccess && metafield.state !== 'enabled' && isFetching);

  return (
    <div className="rounded-md border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between border-gray-200 border-b px-4 py-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900 text-sm">{metafield.schema.name}</p>
            <ExternalLink
              link={metafield.schema.documentationUrl}
              ariaLabel={metafield.schema.name}
            >
              <Icon name="infoCircle" size="sm" />
            </ExternalLink>
          </div>
          <Text size="sm" variant="secondary">
            {metafield.schema.description}
          </Text>
        </div>
        {metafield.state === 'enabled' ? (
          <Icon name="checkCircle" size="md" />
        ) : (
          <Button
            variant="secondary"
            loading={isEnabling}
            onClick={() => enableMetafield.mutate(metafield.schema.key)}
          >
            {t(`${METAFIELDS_I18N}.enable`)}
          </Button>
        )}
      </div>
    </div>
  );
}

export function MetafieldGroupCard({
  group,
  isFetching,
}: {
  group: MetafieldGroup;
  isFetching: boolean;
}) {
  return (
    <Card initialCollapsed={false}>
      <Card.Header headingLevel="h2" collapsible>
        {displayGroupName(group.name)}
      </Card.Header>
      <Card.Body>
        <div className="flex flex-col gap-4">
          {group.metafields.map((metafield) => (
            <MetafieldRow
              key={metafield.schema.key}
              metafield={metafield}
              isFetching={isFetching}
            />
          ))}
        </div>
      </Card.Body>
    </Card>
  );
}
